import { NodeModel } from '@minoru/react-dnd-treeview';
import { EmployeeNodeModel } from '../../models/groupedEmployee.model';
import {
    InterruptionModel,
    InterruptionsListModel,
} from '../../models/interruptions/interruptionList.model';
import {
    WorkshiftSchedulingModel,
    WorkshiftsListModel,
} from '../../models/workshifts/workshif-scheduling/workshiftSchedulingList.model';
import { getChildNodes, getDatesBetweenDates } from '../../utils';

type Props = {
    grid: any;
    treeEmployees: NodeModel[];
    startDate: string;
    endDate: string;
    interruptionList: InterruptionsListModel;
    workshiftShedulingList: WorkshiftsListModel;
    openMultipleCostCentersByHourRangesModal: (
        initDate: string,
        lastDate: string,
        employee: EmployeeNodeModel[],
        costCenters: Array<{
            costCenterId: number;
            costCenterName: string;
            startTime: string;
            endTime: string;
        }>,
        parentNodeSelected: number[]
    ) => void;
    getInterruptionsList: (
        startDate: string,
        endDate: string,
        employeeID: string | number,
        interruptionList: InterruptionsListModel
    ) => InterruptionModel[];
    getWorkshiftScheduling: (
        date: string | number,
        employeeID: string | number,
        workshiftShedulingList: WorkshiftsListModel
    ) => WorkshiftSchedulingModel | undefined;
};

export const controllerMultipleCostCentersByHourRangesModal = ({
    grid,
    treeEmployees,
    startDate,
    endDate,
    interruptionList,
    workshiftShedulingList,
    openMultipleCostCentersByHourRangesModal,
    getInterruptionsList,
    getWorkshiftScheduling,
}: Props) => {
    let recoveredData = window.localStorage.getItem('selection-range');
    let row1 = grid.selectedRanges[0].row;
    let row2 = grid.selectedRanges[0].row2;
    let col1 = grid.selectedRanges[0].col - 2;
    let col2 = grid.selectedRanges[0].col2 - 2;

    if (row1 > row2) {
        let aux = row1;
        row1 = row2;
        row2 = aux;
    }

    if (col1 > col2) {
        let aux = col1;
        col1 = col2;
        col2 = aux;
    }

    let initDate = getDatesBetweenDates(startDate, endDate)[col1];
    let lastDate = getDatesBetweenDates(startDate, endDate)[col2];

    let selectedEmployees = getChildNodes(treeEmployees).slice(row1, row2 + 1);

    let dates = getDatesBetweenDates(initDate, lastDate);

    let workshiftIds: number[] = [];
    let interruptions: number[] = [];
    let parentNodeSelected: number[] = [];
    let assignedCostCenters: Array<{
        employeeId: number;
        date: string;
        costCenterId: number;
        costCenterName: string;
        startTime: string;
        endTime: string;
        id: number;
    }> = [];

    const extractAssignedCostCenters = (emps: NodeModel[], dateRange: string[]) => {
        const costCenterMap = new Map();

        emps.forEach((emp) => {
            dateRange.forEach((date) => {
                workshiftShedulingList.schedulings.forEach((workshift) => {
                    if (
                        workshift.employeeId === emp.id &&
                        workshift.costCenters &&
                        Array.isArray(workshift.costCenters)
                    ) {
                        workshift.costCenters.forEach((cc: any) => {
                            const ccDisplayDate = cc.displayDate.split('T')[0];
                            if (ccDisplayDate === date) {
                                const key = `${cc.id}-${date}`;

                                if (!costCenterMap.has(key)) {
                                    costCenterMap.set(key, {
                                        employeeId: emp.id,
                                        date: date, 
                                        costCenterId: cc.costCenterId,
                                        costCenterName: cc.costCenterName,
                                        startTime: cc.startDate.split('T')[1],  
                                        endTime: cc.endDate.split('T')[1],      
                                        id: cc.id,
                                        workshiftSchedulingId: workshift.id,
                                    });
                                }
                            }
                        });
                    }
                });
            });
        });

        return Array.from(costCenterMap.values());
    }

    if (recoveredData) {
        let data: Array<{
            employeeId: string | number;
            text: string;
            date: string;
            parent: string;
        }> = JSON.parse(recoveredData);

        data.forEach((emp) => {
            let workshiftId = getWorkshiftScheduling(
                emp.date,
                emp.employeeId,
                workshiftShedulingList
            )?.id;

            if (workshiftId) {
                workshiftIds.push(workshiftId);
            }
        });

        data.forEach(({ date, employeeId }) => {
            let interruption = getInterruptionsList(
                date,
                date,
                employeeId,
                interruptionList
            );

            interruption.forEach((interr) => {
                interruptions.push(interr.id);
            });
        });

        data.forEach((emp) => {
            if (!parentNodeSelected.includes(Number(emp.parent))) {
                parentNodeSelected.push(Number(emp.parent));
            }
        });

        const selectedDates = data.map(d => d.date);

        assignedCostCenters = extractAssignedCostCenters(
            data.map((emp) => ({
                id: emp.employeeId,
                parent: '',
                text: emp.text,
                droppable: false,
            })),
            selectedDates
        );

        let employees: NodeModel[] = data.map((emp) => ({
            id: emp.employeeId,
            parent: '',
            text: emp.text,
            droppable: false,
        }));

        if (workshiftIds.length > 0 || interruptions.length > 0) {
            openMultipleCostCentersByHourRangesModal(initDate, lastDate, employees, assignedCostCenters, parentNodeSelected);
        }
    } else {
        for (let emp = 0; emp < selectedEmployees.length; emp++) {
            for (let date = 0; date < dates.length; date++) {
                let workshiftId = getWorkshiftScheduling(
                    dates[date],
                    selectedEmployees[emp].id,
                    workshiftShedulingList
                );

                if (workshiftId?.id) {
                    workshiftIds.push(workshiftId.id);
                }
            }
        }

        selectedEmployees.forEach((emp: any) => {
            if (!parentNodeSelected.includes(Number(emp.parent))) {
                parentNodeSelected.push(Number(emp.parent));
            }
        });

        assignedCostCenters = extractAssignedCostCenters(
            selectedEmployees,
            dates
        );

        if (initDate && lastDate && selectedEmployees.length > 0) {
            openMultipleCostCentersByHourRangesModal(
                initDate,
                lastDate,
                selectedEmployees,
                assignedCostCenters,
                parentNodeSelected
            );
        }
    }
};